import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MobileLayout from '../MobileLayout';
import MobileReportChat from '../MobileReportChat';
import { apiGet } from '../api';

interface FullReport {
  id: string;
  title?: string;
  name?: string;
  workflowType?: string;
  content?: string;
  result?: string;
  output?: string;
  summary?: string;
  customerName?: string;
  customer?: { id: string; name: string; company?: string };
  customerId?: string;
  createdAt?: string;
}

const PRIMARY = '#1993e5';

// Keys that should be hidden on mobile — citations have no PDF-jump affordance
// here, and source-document references don't navigate anywhere useful.
const HIDDEN_KEYS = new Set([
  'citations',
  'citation',
  'source_documents',
  'sourceDocuments',
  'source_document_ids',
  'sourceDocumentIds',
  'docId',
  'docIds',
  'documentIds',
  'document_ids',
  'responseMetadata',
]);

const stripFences = (s: string): string =>
  s.replace(/^\s*```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();

// Strip `[CITE: "..."]` markers (and the inner quoted reference) plus the
// `📑` emoji buttons that the desktop substitutes inline. Mobile has no PDF
// viewer, so they add noise without value.
const stripCitations = (s: string): string =>
  s
    .replace(/\[CITE:\s*"(?:[^"\\]|\\.)*"\s*\]/g, '')
    .replace(/📑/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();

const tryParseJson = (s: string): unknown | null => {
  if (!s) return null;
  const cleaned = stripFences(s);
  if (!cleaned.startsWith('{') && !cleaned.startsWith('[')) return null;
  try { return JSON.parse(cleaned); } catch { return null; }
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const humanLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const renderScalar = (v: unknown): React.ReactNode => {
  if (v === null || v === undefined || v === '') return <span className="text-slate-400">—</span>;
  if (typeof v === 'boolean') return <span className="font-medium">{v ? 'Yes' : 'No'}</span>;
  if (typeof v === 'number') return <span className="font-medium tabular-nums">{v.toLocaleString()}</span>;
  const cleaned = stripCitations(String(v));
  if (!cleaned) return <span className="text-slate-400">—</span>;
  return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{cleaned}</span>;
};

interface JsonNodeProps { value: unknown; depth?: number; }

const JsonNode: React.FC<JsonNodeProps> = ({ value, depth = 0 }) => {
  if (!isPlainObject(value) && !Array.isArray(value)) {
    return <div className="text-sm text-slate-700 dark:text-slate-200">{renderScalar(value)}</div>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <div className="text-sm text-slate-400">—</div>;
    const allScalar = value.every((v) => !isPlainObject(v) && !Array.isArray(v));
    if (allScalar) {
      return (
        <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700 dark:text-slate-200">
          {value.map((v, i) => (
            <li key={i}>{renderScalar(v)}</li>
          ))}
        </ul>
      );
    }
    return (
      <div className="space-y-2">
        {value.map((v, i) => (
          <div
            key={i}
            className="rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700"
          >
            <JsonNode value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  const entries = Object.entries(value).filter(([k, v]) => {
    if (HIDDEN_KEYS.has(k)) return false;
    // Also drop empty arrays/objects to reduce visual noise.
    if (Array.isArray(v) && v.length === 0) return false;
    if (isPlainObject(v) && Object.keys(v).length === 0) return false;
    return true;
  });

  if (entries.length === 0) return null;

  return (
    <dl className="space-y-2.5">
      {entries.map(([k, v]) => {
        const nested = isPlainObject(v) || Array.isArray(v);
        return (
          <div
            key={k}
            className={
              nested
                ? 'rounded-lg bg-white p-3 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700'
                : ''
            }
          >
            <dt
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: depth === 0 ? PRIMARY : '#64748b' }}
            >
              {humanLabel(k)}
            </dt>
            <dd className={nested ? 'mt-2' : 'mt-0.5 text-sm text-slate-700 dark:text-slate-200'}>
              <JsonNode value={v} depth={depth + 1} />
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

const MarkdownBody: React.FC<{ text: string }> = ({ text }) => (
  <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:text-slate-900 dark:prose-headings:text-white prose-p:text-slate-700 dark:prose-p:text-slate-200 prose-li:text-slate-700 dark:prose-li:text-slate-200 prose-strong:text-slate-900 dark:prose-strong:text-white prose-a:text-primary prose-table:text-sm prose-th:bg-slate-100 dark:prose-th:bg-slate-800 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-td:border prose-th:border prose-td:border-slate-200 prose-th:border-slate-200 dark:prose-td:border-slate-700 dark:prose-th:border-slate-700"
       style={{ ['--tw-prose-links' as any]: PRIMARY }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

const RenderedBody: React.FC<{ raw: string }> = ({ raw }) => {
  const parsed = useMemo(() => tryParseJson(raw), [raw]);
  if (parsed === null) {
    return <MarkdownBody text={stripCitations(stripFences(raw))} />;
  }
  if (!isPlainObject(parsed) && !Array.isArray(parsed)) {
    return <div className="text-sm">{renderScalar(parsed)}</div>;
  }
  return <JsonNode value={parsed} />;
};

const MobileReportView: React.FC = () => {
  const { reportId = '' } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<FullReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    apiGet<FullReport>(`/comparison-reports/${reportId}`)
      .then((r) => { if (!cancel) setReport(r); })
      .catch((e) => { if (!cancel) setError(e instanceof Error ? e.message : 'Failed to load report'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [reportId]);

  const body = report?.content || report?.result || report?.output || report?.summary || '';
  const customerLabel = report?.customerName || report?.customer?.company || report?.customer?.name;

  return (
    <MobileLayout title={report?.title || report?.name || 'Report'} back>
      {loading && <div className="px-3 py-6 text-center text-sm text-slate-500">Loading…</div>}
      {error && (
        <div className="mx-3 my-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      {report && (
        <article className="px-3 pb-6 pt-3">
          <header className="mb-3 flex items-start justify-between gap-3 border-b border-slate-200 pb-3 dark:border-slate-800">
            <div className="min-w-0 flex-1">
              {customerLabel && (
                <div className="text-sm font-semibold text-slate-900 dark:text-white">{customerLabel}</div>
              )}
              {report.workflowType && (
                <div
                  className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: PRIMARY }}
                >
                  {humanLabel(report.workflowType)}
                </div>
              )}
              {report.createdAt && (
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {new Date(report.createdAt).toLocaleString()}
                </div>
              )}
            </div>
            {body && (
              <button
                type="button"
                onClick={() => setChatOpen(true)}
                className="flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-sm active:opacity-90"
                style={{ backgroundColor: PRIMARY }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                  <path d="M20 2H4a2 2 0 0 0-2 2v14l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-9 12H7v-2h4v2zm6-4H7V8h10v2z" />
                </svg>
                Ask AI
              </button>
            )}
          </header>

          {body ? (
            <RenderedBody raw={body} />
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              This report has no body content.
            </div>
          )}
        </article>
      )}

      {report && body && (
        <MobileReportChat
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          reportId={report.id}
          reportTitle={report.title || report.name}
          reportContent={body}
          workflowType={report.workflowType}
          customerId={report.customerId || report.customer?.id}
        />
      )}
    </MobileLayout>
  );
};

export default MobileReportView;
